import {
  CAPTURE_WORKFLOW_ID,
  CAPTURE_WORKFLOW_NAME,
} from "../actionItemHelpers";
import { createActionItem } from "../../businessModeService";

/**
 * Create an action item from typed text (optimistic UI; no progress callbacks).
 *
 * @example
 * // Input
 * await createActionItemFromManualText({ actionItemText: "Follow up with legal" });
 * // Output
 * // { workflowId: "__capture__", workflowName: "Captured", action_title: "", created: { key: "...", file_id: "manual::..." } }
 *
 * @example
 * // Input — scoped to a digest workflow filter
 * await createActionItemFromManualText({
 *   actionItemText: "Email legal about NDA",
 *   workflowId: "2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1",
 *   workflowName: "Gmail digest",
 * });
 * // Output
 * // { workflowId: "2af0f4f3-...", workflowName: "Gmail digest", created: { ... } }
 */
export const createActionItemFromManualText = async ({
  actionItemText,
  actionTitle = "",
  sourceSnippet = "",
  pageUrl = "",
  workflowId = "",
  workflowName = "",
  dueDate = "",
  messageSentAt = "",
  actionCategory = "",
  aiAssistType = "",
  aiAssistReason = "",
  fullSourceText = "",
} = {}) => {
  const text = String(actionItemText || "").trim();
  if (!text) {
    throw new Error("Enter a task description first.");
  }

  const wid = String(workflowId || "").trim();
  const providedWorkflowName = String(workflowName || "").trim();

  const created = await createActionItem({
    actionItemText: text,
    ...(String(actionTitle || "").trim() ? { actionTitle: String(actionTitle).trim() } : {}),
    ...(String(sourceSnippet || "").trim() ? { sourceSnippet: String(sourceSnippet).trim() } : {}),
    ...(String(pageUrl || "").trim() ? { pageUrl: String(pageUrl).trim() } : {}),
    ...(wid ? { workflowId: wid } : {}),
    ...(String(dueDate || "").trim() ? { dueDate: String(dueDate).trim() } : {}),
    ...(String(messageSentAt || "").trim() ? { messageSentAt: String(messageSentAt).trim() } : {}),
    ...(String(actionCategory || "").trim() ? { actionCategory: String(actionCategory).trim() } : {}),
    ...(String(aiAssistType || "").trim() ? { aiAssistType: String(aiAssistType).trim() } : {}),
    ...(String(aiAssistReason || "").trim() ? { aiAssistReason: String(aiAssistReason).trim() } : {}),
    ...(String(fullSourceText || "").trim() ? { fullSourceText: String(fullSourceText).trim() } : {}),
  });

  const resolvedWorkflowId =
    String(created.workflow_id || wid || CAPTURE_WORKFLOW_ID).trim() || CAPTURE_WORKFLOW_ID;
  const resolvedWorkflowName =
    String(created.workflow_name || providedWorkflowName || "").trim() ||
    (wid ? providedWorkflowName || wid : CAPTURE_WORKFLOW_NAME);

  return {
    workflowId: resolvedWorkflowId,
    workflowName: resolvedWorkflowName,
    action_title: String(created.action_title || actionTitle || "").trim(),
    created,
  };
};
